import api from '@/services/api';
import { useSyncStore } from '@/store/syncStore';

let pingTimer: ReturnType<typeof setInterval> | null = null;
let consecutiveFailures = 0;

export function isNetworkError(err: unknown): boolean {
  const e = err as { code?: string; message?: string; response?: unknown };
  if (!e) return true;
  if (!e.response && (e.message === 'Network Error' || e.code === 'ERR_NETWORK' || e.code === 'ECONNABORTED')) {
    return true;
  }
  return false;
}

/** Kick Local First auto-drain without creating a static import cycle. */
function kickAutoSync() {
  void import('./syncWorker').then((m) => m.maybeAutoSync());
}

export async function pingServer(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    useSyncStore.getState().setServerReachable(false);
    return false;
  }
  try {
    await api.get('/method/saas_pos.api.offline_store.get_pos_sync_status', { timeout: 3000 });
    consecutiveFailures = 0;
    const wasReachable = useSyncStore.getState().isServerReachable;
    useSyncStore.getState().setServerReachable(true);
    // Reconnected → always drain. While online with pending (not only failed) → drain.
    const { pendingQueueCount, posSyncMode } = useSyncStore.getState();
    if (
      posSyncMode === 'Local First Auto Sync' &&
      (!wasReachable || pendingQueueCount > 0)
    ) {
      kickAutoSync();
    }
    return true;
  } catch (err) {
    if (isNetworkError(err)) {
      consecutiveFailures += 1;
      // Mark unreachable immediately when offline checkout must not wait on retries.
      if (consecutiveFailures >= 1) {
        useSyncStore.getState().setServerReachable(false);
      }
      return false;
    }
    // Auth or other HTTP errors still mean server is reachable
    consecutiveFailures = 0;
    const wasReachable = useSyncStore.getState().isServerReachable;
    useSyncStore.getState().setServerReachable(true);
    if (!wasReachable && useSyncStore.getState().posSyncMode === 'Local First Auto Sync') {
      kickAutoSync();
    }
    return true;
  }
}

export function startConnectivityMonitor() {
  if (typeof window === 'undefined') return;

  const onOnline = () => {
    useSyncStore.getState().setOnline(true);
    // Ping then auto-drain Local First pending queue (sale made while offline).
    void (async () => {
      const ok = await pingServer();
      if (ok) kickAutoSync();
    })();
  };
  const onOffline = () => {
    useSyncStore.getState().setOnline(false);
    useSyncStore.getState().setServerReachable(false);
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  void pingServer();
  if (pingTimer) clearInterval(pingTimer);
  pingTimer = setInterval(() => {
    void pingServer();
  }, 15000);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
  };
}
